import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import AdminStarterModal from "../../components/modals/AdminStarterModal";
import TextInput from "../../components/forms/TextInput";
import { useAuth } from "../../js/hooks/useAuth";
import { setCredential } from "../../js/db/credentials";
import { useJourneyLive } from "../../js/hooks/useJourneyLive";
import { listAgentsForAdmin, listBrokers } from "../../js/api/adminDesk";
import { listSuggestions } from "../../js/api/suggestions";
import { listBetaFeedback, listBetaTesters } from "../../js/api/betaFeedback";
import { openSampleArchivePdf, openSampleReportPdf, openSampleSaleClosePdf } from "../../js/api/samplePdf";
import { manualsUrl, betaOnboardingUrl, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";
import { buildAdminOverview, ensureAccounts, listAccounts, money } from "../../js/accounts";

export default function AdminDashboard() {
  const { session } = useAuth();
  const [pulse, setPulse] = useState(0);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [passwordNote, setPasswordNote] = useState("");
  const brokers = useMemo(() => listBrokers(), [pulse]);
  const agents = useMemo(() => listAgentsForAdmin(), [pulse]);
  const suggestionCount = useMemo(() => listSuggestions().length, [pulse]);
  const betaCount = useMemo(() => listBetaFeedback().length, [pulse]);
  const testerCount = useMemo(() => listBetaTesters().length, [pulse]);
  const accountOverview = useMemo(() => {
    ensureAccounts();
    return buildAdminOverview(listAccounts());
  }, [pulse]);

  useJourneyLive(() => setPulse((n) => n + 1));

  function savePassword(event) {
    event.preventDefault();
    const next = password.trim();
    if (!session?.userId) {
      setPasswordNote("Sign in again to change this password.");
      return;
    }
    if (next.length < 8) {
      setPasswordNote("Use at least 8 characters.");
      return;
    }
    if (next !== confirm.trim()) {
      setPasswordNote("The two passwords do not match.");
      return;
    }
    setCredential(session.userId, next);
    setPassword("");
    setConfirm("");
    setPasswordNote("Password updated on this device.");
  }

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p className="page-lead">
            Development context, desk previews as each role sees them, sample reports, the site
            map, and suggestions. Client and broker notes live in Messages.
          </p>
        </div>
      </div>

      <section className="desk-panel card" id="account-management">
        <h2>Account Management</h2>
        <p className="muted">
          Unified admin desk — {accountOverview.total} accounts, {accountOverview.suspended} suspended, est. MRR{" "}
          {money(accountOverview.mrr)} on this device.
        </p>
        <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
          <Link className="primary-btn" to="/admin/accounts">
            Open Account Management
          </Link>
          <Link className="ghost-btn" to="/admin/accounts?tab=directory&status=suspended">
            Suspended accounts
          </Link>
          <Link className="ghost-btn" to="/admin/users">
            Users & password reset
          </Link>
        </div>
      </section>

      <section className="desk-panel card" id="hub-admin">
        <h2>The Hub — listings</h2>
        <p className="muted">
          Program desk only. Edit listing titles, event URLs, addresses, and contacts. Convention Hall is for
          browsing rooms; listing edits live on Hub Review.
        </p>
        <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
          <Link className="primary-btn" to="/admin/hub#hub-listing-editor">
            Hub Review — add or edit listings
          </Link>
          <Link className="ghost-btn" to="/admin/founders">
            Founder&apos;s View
          </Link>
          <Link className="ghost-btn" to="/admin/hub/hall">
            Convention Hall
          </Link>
        </div>
      </section>

      <section className="desk-panel card" id="admin-password">
        <h2>Admin password</h2>
        <p className="muted">
          Change the password for {session?.email || "this admin account"} on this device. It is
          not emailed and is not stored in the login form.
        </p>
        <form onSubmit={savePassword} autoComplete="off">
          <TextInput
            label="New password"
            name="adminPassword"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
          <TextInput
            label="Confirm new password"
            name="adminPasswordConfirm"
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
          />
          {passwordNote ? <p className="confirm-banner">{passwordNote}</p> : null}
          <button type="submit" className="primary-btn">
            Save password
          </button>
        </form>
      </section>

      <section className="desk-panel card" id="desk-previews">
        <h2>Desk Previews</h2>
        <p className="muted">Open each desk scoped the way that person sees it — not a combined dump.</p>
        <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
          {agents.map((agent) => (
            <Link key={agent.id} className="primary-btn" to={`/agent/dashboard?as=${agent.id}`}>
              Agent desk · {agent.name}
            </Link>
          ))}
          {brokers.map((broker) => (
            <Link key={broker.id} className="ghost-btn" to={`/broker/dashboard?as=${broker.id}`}>
              Broker desk · {broker.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="desk-panel card" id="sample-reports">
        <h2>Sample Reports</h2>
        <p className="muted">Open as PDF in the browser. These links stay off public and agent navigation.</p>
        <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
          <button type="button" className="primary-btn" onClick={openSampleReportPdf}>
            Sample communication report
          </button>
          <button type="button" className="ghost-btn" onClick={openSampleArchivePdf}>
            Sample archive report
          </button>
          <button type="button" className="ghost-btn" onClick={openSampleSaleClosePdf}>
            Sample sale close report
          </button>
        </div>
      </section>

      <section className="desk-panel card" id="role-manuals">
        <h2>Role manuals</h2>
        <p className="muted">
          Four books live on DreamHomeJourney behind a sign-in. This desk does not host the manuals.
        </p>
        <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
          <a className="primary-btn" href={manualsUrl(undefined, session)} {...JOURNEY_LINK_PROPS}>
            All four guides
          </a>
          <a className="ghost-btn" href={manualsUrl("buyer", session)} {...JOURNEY_LINK_PROPS}>
            Buyer
          </a>
          <a className="ghost-btn" href={manualsUrl("seller", session)} {...JOURNEY_LINK_PROPS}>
            Seller
          </a>
          <a className="ghost-btn" href={manualsUrl("agent", session)} {...JOURNEY_LINK_PROPS}>
            Agent
          </a>
          <a className="ghost-btn" href={manualsUrl("broker", session)} {...JOURNEY_LINK_PROPS}>
            Broker
          </a>
        </div>
      </section>

      <section className="desk-panel card" id="sitemap">
        <h2>Site Map</h2>
        <p className="muted">
          How REChecklist.pro and DreamHomeJourney.online connect, plus every current route on both
          origins.
        </p>
        <Link className="primary-btn" to="/admin/sitemap">
          Open site map
        </Link>
      </section>

      <section className="desk-panel card" id="integrity">
        <h2>Deployment Integrity</h2>
        <p className="muted">
          Seven Walls of Steel — Gatekeeper, dual-side backups, comps, immutable ledger, freeze
          windows, staging≡production, and Guardian. CLI gates block unsafe releases.
        </p>
        <Link className="primary-btn" to="/admin/integrity">
          Open integrity desk
        </Link>
      </section>

      <section className="desk-panel card" id="beta-feedback">
        <h2>Beta program</h2>
        <p className="muted">
          Invitation-only. The onboarding flow is not linked from the public site. Open a preview
          to walk the tester path, or read program notes.
        </p>
        <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
          <a className="primary-btn" href={betaOnboardingUrl(session)} {...JOURNEY_LINK_PROPS}>
            Beta Onboarding Preview
          </a>
          <Link className="ghost-btn" to="/admin/beta">
            Open beta inbox
          </Link>
        </div>
        <p className="muted" style={{ marginTop: "0.75rem" }}>
          {testerCount
            ? `${testerCount} tester${testerCount === 1 ? "" : "s"} on this device.`
            : "No Beta testers on this device yet."}{" "}
          {betaCount
            ? `${betaCount} feedback note${betaCount === 1 ? "" : "s"} in the table.`
            : "The feedback table is empty."}
        </p>
      </section>

      <section className="desk-panel card" id="suggestions">
        <h2>Suggestions</h2>
        <p className="muted">
          {suggestionCount
            ? `${suggestionCount} note${suggestionCount === 1 ? "" : "s"} waiting in the inbox.`
            : "The suggestions inbox is empty. Desk users can send notes from their sidebars."}
        </p>
        <Link className="primary-btn" to="/admin/suggestions">
          Open suggestions inbox
        </Link>
      </section>
      <AdminStarterModal />
    </AppLayout>
  );
}
